import { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FormContainer from '../components/FormContainer';
import Loader from '../components/Loader';
import { useGetUsersQuery, useUpdateUserMutation } from '../slices/usersApiSlice';

const AdminUsersScreen = () => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: users, isLoading, error } = useGetUsersQuery(undefined, {
    skip: !userInfo || !userInfo.isAdmin
  });
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    if (!password) {
      toast.error('Please enter a password');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await updateUser({
        userId: selectedUserId,
        password,
      }).unwrap();

      toast.success('Password updated successfully');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err?.data?.message || err.error || 'Something went wrong');
    }
  };

  if (!userInfo || !userInfo.isAdmin) {
    return null;
  }

  return (
    <FormContainer>
      <h1>Admin: Manage Users</h1>
      <p>Select a user and set their new password.</p>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <div className="alert alert-danger">
          {error?.data?.message || error.error || 'Failed to load users'}
        </div>
      ) : (
        <Form onSubmit={submitHandler}>
          <Form.Group controlId='selectUser' className='my-3'>
            <Form.Label>Select User</Form.Label>
            <Form.Control
              as='select'
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value=''>-- Select User --</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email}) {user.isAdmin ? '[Admin]' : ''}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group controlId='password' className='my-3'>
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type='password'
              placeholder='Enter new password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            ></Form.Control>
          </Form.Group>

          <Form.Group controlId='confirmPassword' className='my-3'>
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control
              type='password'
              placeholder='Confirm new password'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            ></Form.Control>
          </Form.Group>

          <Button type='submit' variant='primary' className='mt-2' disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update Password'}
          </Button>
        </Form>
      )}
    </FormContainer>
  );
};

export default AdminUsersScreen;
